import { HubConnectionBuilder, LogLevel, type RetryContext } from "@microsoft/signalr";

import { authService } from "@mr-portfolio-infrastructure/auth/auth-service";
import type { LiveUpdatesConnection, LiveUpdatesHandlers } from "@mr-portfolio-store/live-updates-store";

export const pricesHubPath = "/hubs/prices";
export const quotesUpdatedEvent = "quotesUpdated";
export const portfolioRecalculatedEvent = "portfolioRecalculated";

const reconnectDelaysMilliseconds = [0, 2000, 10000, 30000];
const startRetryMilliseconds = 30000;

/**
 * Connection to the API's `PricesHub`. The browser cannot send headers on WebSockets, so the access token goes in the
 * `access_token` query parameter. A failed start and a lost connection are retried until the connection is stopped.
 */
export function createPricesHubConnection(handlers: LiveUpdatesHandlers): LiveUpdatesConnection {
  const hubConnection = new HubConnectionBuilder()
    .withUrl(pricesHubPath, { accessTokenFactory: readAccessToken })
    .withAutomaticReconnect({ nextRetryDelayInMilliseconds: getReconnectDelay })
    .configureLogging(LogLevel.Warning)
    .build();
  let isStopped = false;
  let hasStartFailed = false;
  let startRetryTimeoutId: number | undefined = undefined;

  hubConnection.on(quotesUpdatedEvent, () => handlers.onQuotesUpdated());
  hubConnection.on(portfolioRecalculatedEvent, (portfolioId: number) => handlers.onPortfolioRecalculated(portfolioId));
  hubConnection.onreconnected(() => handlers.onReconnected());

  const start = async (): Promise<void> => {
    startRetryTimeoutId = undefined;

    try {
      await hubConnection.start();
    } catch {
      hasStartFailed = true;

      if (!isStopped) {
        startRetryTimeoutId = window.setTimeout(() => void start(), startRetryMilliseconds);
      }

      return;
    }

    if (hasStartFailed) {
      hasStartFailed = false;
      handlers.onReconnected();
    }
  };

  const stop = async (): Promise<void> => {
    isStopped = true;
    window.clearTimeout(startRetryTimeoutId);
    await hubConnection.stop();
  };

  const connection: LiveUpdatesConnection = { start, stop };

  return connection;
}

async function readAccessToken(): Promise<string> {
  const accessToken = (await authService.getAccessToken()) ?? "";

  return accessToken;
}

function getReconnectDelay(retryContext: RetryContext): number {
  const delayIndex = Math.min(retryContext.previousRetryCount, reconnectDelaysMilliseconds.length - 1);
  const reconnectDelay = reconnectDelaysMilliseconds[delayIndex] ?? startRetryMilliseconds;

  return reconnectDelay;
}
